import React from 'react'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import {
  Box,
  FormControl,
  FormHelperText,
  IconButton,
  InputBase,
  InputBaseProps,
  InputLabel,
  styled,
} from '@mui/material'

import { lightTheme } from '~createTheme'

interface NumberInputProps extends Omit<InputBaseProps, 'onChange'> {
  placeholder?: string
  label?: string
  helperText?: string
  errorText?: string
  value?: number | null
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  min?: number
  max?: number
  step?: number
}

const StyledFormControl = styled(FormControl)({
  width: '100%',
  position: 'relative',
  margin: lightTheme.spacing(0, 0),
})

const NumberInputBase = styled(InputBase)(({ theme, error, readOnly }) => ({
  width: '100%',
  'label + &': {
    marginTop: theme.spacing(3),
  },
  '& .MuiInputBase-input': {
    borderRadius: 4,
    position: 'relative',
    border: '1px solid',
    borderColor: error
      ? theme.palette.error.main
      : readOnly
        ? theme.palette.text.disabled
        : theme.palette.divider,
    backgroundColor: readOnly ? theme.palette.action.disabledInput : 'white',
    fontSize: 16,
    padding: '10px 12px',
    paddingRight: '60px', // Make room for the chevron buttons
    transition:
      !readOnly && theme.transitions.create(['border-color', 'background-color', 'box-shadow']),
    fontFamily: theme.typography.body2.fontFamily,
    '&:hover': !readOnly && {
      borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
    },
    '&:focus-visible': !readOnly && {
      border: '2px solid #ffffff',
      outline: error ? `2px solid ${theme.palette.error.main}` : '2px solid #0894cf',
      boxShadow: error
        ? '0 0 0 2px #ee2a7b1d, 0 0 0 4px #ee2a7b1d'
        : '0 0 0 2px #98e0ff, 0 0 0 4px #98e0ff',
    },
  },
}))

const ChevronButton = styled(IconButton)(({ theme }) => ({
  padding: 4,
  position: 'absolute',
  right: 2,
  width: 28,
  height: 20,
  minWidth: 28,
  color: theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&.Mui-disabled': {
    color: theme.palette.action.disabled,
  },
}))

const InputLabelStyled = styled(InputLabel)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: 18,
  letterSpacing: theme.typography.body2.letterSpacing,
  fontFamily: theme.typography.fontFamily,
  '&.Mui-focused': {
    color: theme.palette.text.primary,
  },
}))

const NumberInput: React.FC<NumberInputProps> = ({
  label,
  placeholder = '',
  helperText,
  errorText,
  onChange,
  min = 0,
  max = Infinity,
  step = 1,
  value = null,
  disabled,
  ...props
}) => {
  // Create a ref to the input element
  const inputRef = React.useRef<HTMLInputElement>(null)
  const inputId = React.useMemo(() => `number-input-${label?.replace(' ', '-')}`, [label])

  // Prevent mouse wheel from changing number input value
  const wheelHandler = { onWheel: (e: React.WheelEvent): void => (e.target as HTMLElement).blur() }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    onChange(event)
  }

  const getCurrentValue = (): number | null => {
    // @ts-ignore
    if (value === null || value === undefined || value === '') return null
    if (typeof value === 'number') return value
    if (typeof value === 'string' && value !== '') {
      const num = parseFloat(value)
      return isNaN(num) ? null : num
    }
    return null
  }

  const createSyntheticEvent = (newValue: number | null): React.ChangeEvent<HTMLInputElement> => {
    if (inputRef.current) {
      inputRef.current.value = newValue === null ? '' : newValue.toString()
      const event = new Event('change', { bubbles: true })
      Object.defineProperty(event, 'target', { value: inputRef.current })
      return event as unknown as React.ChangeEvent<HTMLInputElement>
    }
    // Fallback if ref is not available
    return {
      target: { value: newValue === null ? '' : newValue.toString() },
      currentTarget: { value: newValue === null ? '' : newValue.toString() },
    } as React.ChangeEvent<HTMLInputElement>
  }

  const increment = (): void => {
    const currentValue = getCurrentValue()
    const newValue = Math.min((currentValue ?? 0) + step, max)
    onChange(createSyntheticEvent(newValue))
  }

  const decrement = (): void => {
    const currentValue = getCurrentValue()
    const newValue = Math.max((currentValue ?? 0) - step, min)
    onChange(createSyntheticEvent(newValue))
  }

  const isAtMax = (getCurrentValue() ?? 0) >= max
  const isAtMin = (getCurrentValue() ?? 0) <= min

  return (
    <StyledFormControl error={!!errorText}>
      {label && (
        <InputLabelStyled shrink htmlFor={inputId}>
          {label}
        </InputLabelStyled>
      )}
      <Box sx={{ position: 'relative' }}>
        <NumberInputBase
          id={inputId}
          inputRef={inputRef}
          type='number'
          placeholder={placeholder}
          error={!!errorText}
          onChange={handleChange}
          value={value === null ? '' : value}
          disabled={disabled}
          inputProps={{
            min,
            max,
            step,
            id: 'number-input',
            'aria-labelledby': label ? inputId : undefined,
            'aria-describedby': errorText || helperText ? `${inputId}-helper-text` : undefined,
            '&:hover': {
              borderColor: lightTheme.palette.primary.main,
            },
          }}
          {...wheelHandler}
          {...props}
        />
        <Box
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: 32,
            borderLeft: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <ChevronButton
            aria-label='Increase number'
            onClick={increment}
            disabled={disabled || isAtMax}
            size='small'
            sx={{ top: 0 }}
            role='button'
            aria-roledescription='stepper'
          >
            <KeyboardArrowUpIcon fontSize='small' />
          </ChevronButton>
          <ChevronButton
            aria-label='Decrease number'
            onClick={decrement}
            disabled={disabled || isAtMin}
            size='small'
            sx={{ bottom: 0 }}
            role='button'
            aria-roledescription='stepper'
          >
            <KeyboardArrowDownIcon fontSize='small' />
          </ChevronButton>
        </Box>
      </Box>
      {(errorText || helperText) && (
        <FormHelperText id={`${inputId}-helper-text`} error={!!errorText}>
          {errorText || helperText}
        </FormHelperText>
      )}
    </StyledFormControl>
  )
}

export default NumberInput
